import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API_PATHS } from '../config/api-paths';
import { FinancingRequestApi } from '../tokens/financing-request.token';
import {
  ComplianceRow,
  DocumentRequirement,
  FinancingDraft,
  FinancingEligibility,
  FinancingRequestSubmit,
  FinancingRequestSubmitResponse,
  FinancingSummary,
  PreSubmissionCheck,
  PreSubmissionGate,
  Psd2ReattemptRequest,
  SdfDistributorOption,
  SmeFinancingRequest,
} from '../models/financing.model';

/** The dashboard compliance snapshot wire shape (`ComplianceSnapshotDTO`). */
interface ComplianceSnapshotResponse {
  rows: ComplianceRow[];
}

/**
 * REST-only implementation of `FinancingRequestApi` (EPIC-4 / EM-500, Story 9 — EM-509).
 *
 * Calls:
 *   POST /services/embedded/api/v1/applications          (F-01 submit)
 *   GET  /services/embedded/api/me/dashboard/compliance  (compliance snapshot → eligibility)
 *   GET  /services/embedded/api/me/financing/summary     (risk grade + exposure)
 *
 * Rides the existing Keycloak bearer (attached by `authInterceptor`). Rule 07 / Rule 10 —
 * no Mock fallback; Playwright specs intercept via `page.route()`. Rule 09 — `submit` propagates
 * the raw `HttpErrorResponse`; `eligibility` degrades to `{ available: false }` and
 * `financingSummary` degrades to `null` so the UI renders "—" instead of fabricating values.
 */
@Injectable()
export class FinancingRequestRestService implements FinancingRequestApi {
  private readonly http = inject(HttpClient);

  submit(request: FinancingRequestSubmit): Observable<FinancingRequestSubmitResponse> {
    return this.http.post<FinancingRequestSubmitResponse>(API_PATHS.APPLICATIONS, request);
  }

  eligibility(): Observable<FinancingEligibility> {
    return this.http.get<ComplianceSnapshotResponse>(`${API_PATHS.ME_DASHBOARD}/compliance`).pipe(
      map((snapshot) => ({
        available: true,
        rows: snapshot?.rows ?? [],
      })),
      catchError(() => of({ available: false, rows: [], errorCode: 'eligibility.unavailable' })),
    );
  }

  financingSummary(): Observable<FinancingSummary | null> {
    return this.http
      .get<FinancingSummary>(`${API_PATHS.ME_FINANCING}/summary`)
      .pipe(catchError(() => of(null)));
  }

  /**
   * EM-1606 — the caller's own financing requests. The error is NOT swallowed here: an empty array is
   * a meaningful answer ("no requests yet") and the "My Requests" sub-tab must be able to tell the two
   * apart so a failed load surfaces the backend message (Rule 09) instead of a false empty state.
   */
  myRequests(): Observable<SmeFinancingRequest[]> {
    return this.http
      .get<SmeFinancingRequest[]>(`${API_PATHS.ME_FINANCING}/requests`)
      .pipe(map((rows) => rows ?? []));
  }

  /**
   * EM-1680 — the BRS §4.1.2 gate rows for wizard Step 2. Errors propagate: the wizard fails OPEN
   * (renders what it has, lets the authoritative submit 422 decide) and needs to know the load
   * failed to do so.
   */
  preSubmissionCheck(params: {
    product?: string;
    amount?: number;
    currency?: string;
    debtorCui?: string;
    invoiceCount?: number;
  }): Observable<PreSubmissionCheck> {
    let httpParams = new HttpParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    }
    return this.http.get<PreSubmissionCheck>(`${API_PATHS.ME_FINANCING}/pre-submission-check`, { params: httpParams });
  }

  saveDraft(draft: Partial<FinancingDraft>): Observable<FinancingDraft> {
    return this.http.post<FinancingDraft>(`${API_PATHS.ME_FINANCING}/drafts`, draft);
  }

  updateDraft(id: number, draft: Partial<FinancingDraft>): Observable<FinancingDraft> {
    return this.http.put<FinancingDraft>(`${API_PATHS.ME_FINANCING}/drafts/${id}`, draft);
  }

  getDraft(id: number): Observable<FinancingDraft> {
    return this.http.get<FinancingDraft>(`${API_PATHS.ME_FINANCING}/drafts/${id}`);
  }

  cancelDraft(id: number): Observable<void> {
    return this.http.delete<void>(`${API_PATHS.ME_FINANCING}/drafts/${id}`);
  }

  documentRequirements(params: { product?: string; draftId?: number }): Observable<DocumentRequirement[]> {
    let httpParams = new HttpParams();
    if (params.product) {
      httpParams = httpParams.set('product', params.product);
    }
    if (params.draftId != null) {
      httpParams = httpParams.set('draftId', String(params.draftId));
    }
    return this.http.get<DocumentRequirement[]>(`${API_PATHS.ME_FINANCING}/document-requirements`, { params: httpParams });
  }

  psd2Reattempt(request: Psd2ReattemptRequest): Observable<PreSubmissionGate> {
    return this.http.post<PreSubmissionGate>(`${API_PATHS.ME_FINANCING}/psd2-reattempt`, request);
  }

  sdfDistributors(): Observable<SdfDistributorOption[]> {
    return this.http
      .get<SdfDistributorOption[]>(`${API_PATHS.ME_FINANCING}/enterprise-distributors`)
      .pipe(map((rows) => rows ?? []));
  }
}
